import { createAsyncThunk } from "@reduxjs/toolkit";
import { normalize } from "normalizr";

import magnifiAPI from "../../services/api/magnifi";
import { studioTemplatesGroupByDate, templates } from "../schemas";

export const applyTemplateToClips = createAsyncThunk(
  "clip/applyTemplateToClips",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await magnifiAPI.applyTemplateToClips(payload);

      return response.data;
    } catch (error) {
      console.error(error);
      let errorMsg = error?.response?.status === 501 ? error?.response?.data?.error?.title : `Template Creation Failed`;
      let errStatus = error?.response?.status;
      let isQuotaExhausted = error?.response?.data?.error?.quota_exhausted;
      let quotaEvent = error?.response?.data?.error?.quota_event;

      if (isQuotaExhausted) errorMsg = error?.response?.data?.error?.title;

      // appending error code, with each error, so as to easy to debug
      const errorCode = error?.response?.data?.error?.errorcode;

      return rejectWithValue({
        errorMessage: `${errorCode || 500}-${errorMsg}`,
        errorCode,
        errStatus,
        isQuotaExhausted,
        quotaEvent,
        retryData: error?.response?.data?.error?.retryData,
      });
    }
  },
);

export const validateTemplate = createAsyncThunk("clip/validateTemplate", async (payload, { rejectWithValue }) => {
  try {
    const response = await magnifiAPI.validateTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    let errorMsg = error?.response?.status === 500 ? error?.response?.data?.error?.title : `Template Validation Failed`;

    return rejectWithValue({
      errorMessage: `${errorMsg}`,
      error: true,
    });
  }
});

export const getTemplatesList = createAsyncThunk("clip/getTemplatesList", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplatesList(payload);
    const normalizedData = normalize(response?.data?.data, [templates]);

    return {
      ...response.data,
      ...(response.data.success ? normalizedData : {}),
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const retryTemplateExport = createAsyncThunk("clip/retryTemplateExport", async (payload) => {
  try {
    magnifiAPI.retryTemplateExport(payload);
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplatesListGroupByDate = createAsyncThunk("clip/getTemplatesListGroupByDate", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplatesListGroupByDate(payload);
    const normalizedData = normalize(response?.data?.data, [studioTemplatesGroupByDate]);

    return {
      ...response.data,
      ...(response.data.success ? normalizedData : {}),
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const createTemplate = createAsyncThunk("clip/createTemplate", async (payload) => {
  try {
    const response = await magnifiAPI.createTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const updateTemplate = createAsyncThunk("clip/updateTemplate", async (payload) => {
  try {
    const response = await magnifiAPI.updateTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const deleteTemplate = createAsyncThunk("clip/deleteTemplate", async (payload) => {
  try {
    const response = await magnifiAPI.deleteTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const duplicateTemplate = createAsyncThunk("clip/duplicateTemplate", async (payload) => {
  try {
    const response = await magnifiAPI.duplicateTemplate(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplateMatchAssetsFilter = createAsyncThunk("clip/templateMatchAssetsFilter", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplateMatchAssetsFilter(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const unlockStudioClip = createAsyncThunk("clip/unlockStudioClip", async (payload) => {
  try {
    const response = await magnifiAPI.unlockStudioClip(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplateBucketStatus = createAsyncThunk("clip/getTemplateBucketStatus", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplateBucketStatus(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplateQuota = createAsyncThunk("clip/getTemplateQuota", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplateQuota(payload);

    return response?.data?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplateParentClip = createAsyncThunk("clip/getTemplateParentClip", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplateParentClip(payload);

    return response?.data?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplateContentDetails = createAsyncThunk("clip/getTemplateContentDetails", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplateContentDetails(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplateValidationFields = createAsyncThunk("clip/getTemplateValidationFields", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplateValidationFields(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const updateTemplateValidations = createAsyncThunk("clip/updateTemplateValidations", async (payload) => {
  try {
    const response = await magnifiAPI.updateTemplateValidations(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});

export const getTemplateById = createAsyncThunk("clip/getTemplateById", async (payload) => {
  try {
    const response = await magnifiAPI.getTemplateById(payload);

    return response.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
});
